import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import {
  CHANNEL_LABEL,
  PostboxMessage,
  PostboxMessagesFilters,
  listPostboxMessages,
} from '../../_services/PostboxMessagesService';
import './postbox-messages.css';

const STATUS_OPTIONS = ['', 'PENDING', 'SENT', 'FAILED', 'CANCELLED'];
const CHANNEL_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All channels' },
  { value: '1', label: 'Email' },
  { value: '2', label: 'SMS' },
  { value: '5', label: 'WhatsApp' },
  { value: '3', label: 'Push' },
  { value: '4', label: 'In-app' },
];

const statusPalette = (status: string | null): { bg: string; fg: string } => {
  switch ((status || '').toUpperCase()) {
    case 'PENDING':
      return { bg: '#fff3cd', fg: '#856404' };
    case 'SENT':
      return { bg: '#d4edda', fg: '#155724' };
    case 'DELIVERED':
      return { bg: '#cce5ff', fg: '#004085' };
    case 'FAILED':
      return { bg: '#f8d7da', fg: '#721c24' };
    case 'CANCELLED':
      return { bg: '#e2e3e5', fg: '#383d41' };
    default:
      return { bg: '#f1f3f5', fg: '#212529' };
  }
};

const formatDateTime = (s: string | null): string => {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString();
  } catch {
    return s;
  }
};

const truncate = (s: string | null, n: number): string => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

const PostboxMessagesPage: React.FC = () => {
  const [rows, setRows] = useState<PostboxMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [filters, setFilters] = useState<PostboxMessagesFilters>({});
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [nameFilter, setNameFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const [detailRow, setDetailRow] = useState<PostboxMessage | null>(null);

  const fetchRows = useCallback(
    async (opts: PostboxMessagesFilters = {}) => {
      setLoading(true);
      setError(null);
      try {
        const res = await listPostboxMessages({
          ...filters,
          page,
          pageSize,
          ...opts,
        });
        setRows(res.data || []);
        setTotal(res.pagination?.total || 0);
        setTotalPages(res.pagination?.totalPages || 0);
      } catch (e: any) {
        const data = e?.response?.data;
        const detail = data?.error ? ` — ${data.error}` : '';
        setError((data?.message || e?.message || 'Failed to load') + detail);
      } finally {
        setLoading(false);
      }
    },
    [filters, page, pageSize],
  );

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const applyFilters = () => {
    const next: PostboxMessagesFilters = {};
    if (statusFilter) next.status = statusFilter;
    if (channelFilter) next.channelId = parseInt(channelFilter, 10);
    if (nameFilter.trim()) next.name = nameFilter.trim();
    if (searchFilter.trim()) next.q = searchFilter.trim();
    if (fromDate) next.fromDate = fromDate;
    if (toDate) next.toDate = toDate;
    setFilters(next);
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setChannelFilter('');
    setNameFilter('');
    setSearchFilter('');
    setFromDate('');
    setToDate('');
    setFilters({});
    setPage(1);
  };

  return (
    <div className="postbox-messages-page" style={{ padding: '20px 24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: '1px solid var(--border-default)',
        }}
      >
        <div>
          <h4 style={{ margin: 0, fontWeight: 600 }}>Notification Outbox</h4>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            All transactional emails, SMS, and WhatsApp messages queued by the system.
            Status flips from PENDING → SENT/FAILED when the delivery picker runs.
          </div>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => fetchRows()}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Filters bar */}
      <div className="pbx-filters">
        <div className="pbx-filter" style={{ minWidth: 120 }}>
          <label className="pbx-filter-label">Status</label>
          <select
            className="pbx-filter-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s || 'All'}
              </option>
            ))}
          </select>
        </div>

        <div className="pbx-filter" style={{ minWidth: 130 }}>
          <label className="pbx-filter-label">Channel</label>
          <select
            className="pbx-filter-input"
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
          >
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="pbx-filter" style={{ minWidth: 180 }}>
          <label className="pbx-filter-label">Rule</label>
          <input
            className="pbx-filter-input"
            type="text"
            placeholder="e.g. Substitution"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>

        <div className="pbx-filter pbx-filter-grow" style={{ minWidth: 240 }}>
          <label className="pbx-filter-label">Search</label>
          <input
            className="pbx-filter-input"
            type="text"
            placeholder="Subject / recipient / target"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </div>

        <div className="pbx-filter" style={{ minWidth: 140 }}>
          <label className="pbx-filter-label">From</label>
          <input
            className="pbx-filter-input"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="pbx-filter" style={{ minWidth: 140 }}>
          <label className="pbx-filter-label">To</label>
          <input
            className="pbx-filter-input"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <div className="pbx-filter-actions">
          <Button size="sm" variant="primary" onClick={applyFilters}>
            Apply
          </Button>
          <Button size="sm" variant="secondary" onClick={clearFilters}>
            Clear
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
        {total.toLocaleString()} total messages · Page {page} of {totalPages || 1}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">No postbox messages match the filters.</div>
      ) : (
        <Table responsive>
          <thead>
            <tr>
              <th style={{ width: 60 }}>ID</th>
              <th style={{ width: 80 }}>Channel</th>
              <th>To</th>
              <th>Subject / Message</th>
              <th>Rule</th>
              <th>Status</th>
              <th>Saved</th>
              <th>Sent</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const palette = statusPalette(row.status);
              const channel = row.channelId
                ? CHANNEL_LABEL[row.channelId] || `#${row.channelId}`
                : '—';
              const recipient = row.bcc || row.target || '—';
              return (
                <tr
                  key={row.id}
                  onClick={() => setDetailRow(row)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {row.id}
                  </td>
                  <td>{channel}</td>
                  <td style={{ maxWidth: 220, wordBreak: 'break-all' }}>{recipient}</td>
                  <td style={{ maxWidth: 380 }}>
                    {row.subject ? (
                      <div style={{ fontWeight: 500 }}>{row.subject}</div>
                    ) : null}
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {truncate(row.message, 120)}
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-body)' }}>{row.name || '—'}</td>
                  <td>
                    <span
                      className="msg-status-badge"
                      style={{ background: palette.bg, color: palette.fg }}
                    >
                      {row.status || '—'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDateTime(row.saveDate)}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {formatDateTime(row.sendDate)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1 || loading}
        >
          ← Prev
        </Button>
        <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          {page} / {totalPages || 1}
        </span>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages || loading}
        >
          Next →
        </Button>
      </div>

      <Modal
        show={!!detailRow}
        onHide={() => setDetailRow(null)}
        size="lg"
        dialogClassName="pbx-detail-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="pbx-detail-title">
            <span className="pbx-detail-id">Message #{detailRow?.id}</span>
            {detailRow && (
              <span
                className="pbx-detail-status"
                style={(() => {
                  const p = statusPalette(detailRow.status);
                  return { background: p.bg, color: p.fg };
                })()}
              >
                {detailRow.status || 'UNKNOWN'}
              </span>
            )}
            {detailRow?.name && (
              <span className="pbx-detail-rule">{detailRow.name}</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pbx-detail-body">
          {detailRow && (
            <>
              <div className="pbx-kv">
                <div className="pbx-k">Channel</div>
                <div className="pbx-v">
                  {detailRow.channelId
                    ? CHANNEL_LABEL[detailRow.channelId] || `#${detailRow.channelId}`
                    : '—'}
                </div>
                <div className="pbx-k">Type</div>
                <div className="pbx-v">{detailRow.type || '—'}</div>
                <div className="pbx-k">From</div>
                <div className="pbx-v">
                  {detailRow.source || '—'}
                  {detailRow.sourceDisplayName ? (
                    <span className="pbx-v-muted"> · {detailRow.sourceDisplayName}</span>
                  ) : null}
                </div>
                <div className="pbx-k">To (email)</div>
                <div className="pbx-v">{detailRow.bcc || '—'}</div>
                <div className="pbx-k">Target (phone)</div>
                <div className="pbx-v">{detailRow.target || '—'}</div>
                {detailRow.cc && (
                  <>
                    <div className="pbx-k">Cc</div>
                    <div className="pbx-v">{detailRow.cc}</div>
                  </>
                )}
                <div className="pbx-k">Subject</div>
                <div className="pbx-v">{detailRow.subject || '—'}</div>
                <div className="pbx-k">User ID</div>
                <div className="pbx-v">{detailRow.userId ?? '—'}</div>
                <div className="pbx-k">Enrollment ID</div>
                <div className="pbx-v">{detailRow.enrollmentId ?? '—'}</div>
                <div className="pbx-k">Saved at</div>
                <div className="pbx-v">{formatDateTime(detailRow.saveDate)}</div>
                <div className="pbx-k">Sent at</div>
                <div className="pbx-v">{formatDateTime(detailRow.sendDate)}</div>
                <div className="pbx-k">Delivered at</div>
                <div className="pbx-v">{formatDateTime(detailRow.deliveredDate)}</div>
                {detailRow.sesMessageId && (
                  <>
                    <div className="pbx-k">SES Message ID</div>
                    <div className="pbx-v pbx-mono">{detailRow.sesMessageId}</div>
                  </>
                )}
                {detailRow.sesNotificationType && (
                  <>
                    <div className="pbx-k">SES Notification</div>
                    <div className="pbx-v">{detailRow.sesNotificationType}</div>
                  </>
                )}
              </div>

              <div className="pbx-section-title">Message body</div>
              <pre className="pbx-body-pre">{detailRow.message || '(empty)'}</pre>

              {detailRow.exception && (
                <>
                  <div className="pbx-section-title pbx-section-title-danger">
                    Exception
                  </div>
                  <pre className="pbx-body-pre pbx-body-pre-danger">
                    {detailRow.exception}
                  </pre>
                </>
              )}

              {detailRow.gatewayResponse && (
                <>
                  <div className="pbx-section-title">Gateway response</div>
                  <pre className="pbx-body-pre">{detailRow.gatewayResponse}</pre>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailRow(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PostboxMessagesPage;
